import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import api from "../../api";

const statuses = [
  "assigned",
  "in_progress",
  "submitted",
  "returned",
  "approved",
  "cancelled",
];
const label = (value) => value.replaceAll("_", " ");

export default function WorkQueue({
  projectId,
  revision,
  busy,
  onOpen,
  assignButton,
}) {
  const mobile = useMediaQuery("(max-width:599px)");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [status, setStatus] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const params = new URLSearchParams({ offset: page * size, limit: size });
    if (projectId) params.set("project_id", projectId);
    if (status) params.set("status", status);
    if (overdue) params.set("overdue", "true");
    if (query) params.set("q", query);
    api({ method: "get", url: `/surveys/worklist?${params}` })
      .then(({ data }) => {
        if (!active) return;
        if (page > 0 && page * size >= data.total) {
          setPage(Math.max(0, Math.ceil(data.total / size) - 1));
          return;
        }
        setResult(data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [projectId, revision, page, size, status, overdue, query, retry]);

  const changeFilter = (setter, value) => {
    setter(value);
    setPage(0);
  };
  const clear = () => {
    setSearch("");
    setQuery("");
    setStatus("");
    setOverdue(false);
    setPage(0);
  };
  const filtered = Boolean(query || status || overdue);
  const showRows = !loading && !error && result;
  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} gap={2} sx={{ mb: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            changeFilter(setQuery, search.trim());
          }}
          sx={{ display: "flex", gap: 1, flex: 1 }}
        >
          <TextField
            size="small"
            fullWidth
            label="Search assignments"
            placeholder="Location, project or SM-number"
            value={search}
            inputProps={{ maxLength: 160 }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" disabled={busy}>
            Search
          </Button>
        </Box>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170 }}
          onChange={(e) => changeFilter(setStatus, e.target.value)}
        >
          <MenuItem value="">All statuses</MenuItem>
          {statuses.map((s) => (
            <MenuItem key={s} value={s}>
              {label(s)}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          label="Overdue only"
          control={
            <Switch
              checked={overdue}
              onChange={(e) => changeFilter(setOverdue, e.target.checked)}
            />
          }
        />
        {assignButton}
      </Stack>
      {filtered && (
        <Button onClick={clear} sx={{ mb: 1 }}>
          Clear filters
        </Button>
      )}
      {loading && (
        <CircularProgress size={24} aria-label="Loading assignments" />
      )}
      {error && (
        <Alert
          severity="error"
          action={<Button onClick={() => setRetry(retry + 1)}>Retry</Button>}
        >
          Unable to load assignments. Try again.
        </Alert>
      )}
      {showRows && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {result.summary.total}{" "}
            {result.summary.total === 1 ? "assignment" : "assignments"} in{" "}
            {projectId ? "this project" : "My work"}
            {" · "}
            {result.summary.statuses.submitted || 0} awaiting review
            {" · "}
            {result.summary.statuses.returned || 0} returned
            {" · "}
            {result.summary.overdue} overdue
            {filtered && " · Totals include all statuses and locations."}
          </Typography>
          <TableContainer component={Card}>
            {mobile ? (
              <Box>
                {result.items.map((row) => (
                  <Box
                    key={row.id}
                    sx={{ p: 2, borderBottom: "1px solid #e3e8ee" }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography>SM-{row.id}</Typography>
                      <Chip size="small" label={label(row.status)} />
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{ mt: 1, overflowWrap: "anywhere" }}
                    >
                      {row.location}
                    </Typography>
                    <Typography variant="body2">{row.project_name}</Typography>
                    <Typography variant="body2" sx={{ my: 1 }}>
                      Due {new Date(row.due_date).toLocaleString()}
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={busy}
                      onClick={() => onOpen(row.id)}
                    >
                      {projectId && row.status === "submitted"
                        ? "Review"
                        : "Open"}
                    </Button>
                  </Box>
                ))}
                {!result.items.length && (
                  <Typography sx={{ p: 3 }}>
                    {filtered
                      ? "No assignments match these filters."
                      : "No assignments yet."}
                  </Typography>
                )}
              </Box>
            ) : (
              <Table aria-label="Assignments">
                <TableHead>
                  <TableRow>
                    {["Assignment", "Location", "Due", "Status", ""].map(
                      (h, i) => (
                        <TableCell key={i}>{h}</TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.items.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        SM-{row.id}
                        <Typography variant="caption" display="block">
                          {row.project_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.location}</TableCell>
                      <TableCell>
                        {new Date(row.due_date).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={label(row.status)}
                          color={
                            {
                              submitted: "info",
                              returned: "warning",
                              approved: "success",
                            }[row.status] || "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button disabled={busy} onClick={() => onOpen(row.id)}>
                          {projectId && row.status === "submitted"
                            ? "Review"
                            : "Open"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!result.items.length && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ py: 5, textAlign: "center" }}
                      >
                        {filtered
                          ? "No assignments match these filters."
                          : "No assignments yet."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <TablePagination
              component="div"
              count={result.total}
              page={page}
              rowsPerPage={size}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(e) =>
                changeFilter(setSize, Number(e.target.value))
              }
            />
          </TableContainer>
        </>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 1 }}
      >
        Overdue means assigned, in progress or returned work past its due date.
        Approval is a QC status; it does not publish to clients or trigger
        payment.
      </Typography>
    </Box>
  );
}
